import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  HStack,
  StackDivider,
  Text,
} from "@chakra-ui/react";
import { AuthHelpButton } from "features/auth/AuthHelpButton";
import React from "react";
import { useTranslation } from "react-i18next";
import { CardCloseButton } from "shared/components/card/CardCloseButton";
import { useToast } from "shared/components/toast/useToast";
import { downloadFileBlob } from "shared/utils/utils";

export interface AuthFactorTotpRecoverySetupProps {
  onCancel: () => void;
  onSuccess?: () => void;
  recoveryCodes: string[];
}

export const AuthFactorRecoverySetup: React.FC<
  AuthFactorTotpRecoverySetupProps
> = ({ onCancel, onSuccess, recoveryCodes }) => {
  const { t } = useTranslation(["common", "auth"]);
  const { showToast } = useToast();

  const makeRecoveryFileText = (recoveryCodes: string[]) =>
    t("auth:recoveryCodesFileText", { codes: recoveryCodes.join("\n") });

  const downloadRecoveryFile = () => {
    const blob = new Blob([makeRecoveryFileText(recoveryCodes)], {
      type: "text/plain",
    });
    downloadFileBlob(blob, "maybern_recovery_codes.txt");
  };

  const copyRecoveryFile = async () => {
    const text = makeRecoveryFileText(recoveryCodes);
    await navigator.clipboard.writeText(text);
    showToast({ variant: "success", message: t("common:copied") });
  };

  return (
    <Card width="md">
      <CardHeader>
        <Box>
          <Text textStyle="h4">{t("auth:recoveryCodes")}</Text>
          <Text pt="24px">{t("auth:setup.recoveryCodeInstructions")}</Text>
        </Box>
        <CardCloseButton onClick={onCancel} />
      </CardHeader>
      <CardBody>
        <Box
          borderWidth="1px"
          borderRadius="4px"
          borderColor="gray.300"
          overflow="hidden"
        >
          {recoveryCodes.map((code, index) => (
            <Text
              key={index}
              bg={index % 2 === 0 ? "gray.50" : "white"}
              p="10px"
              textAlign="center"
            >
              {code}
            </Text>
          ))}
          <HStack
            justifyContent="center"
            bg="gray.50"
            borderTopWidth={1}
            borderColor="gray.300"
            divider={<StackDivider borderColor="gray.300" />}
            spacing={0}
          >
            <Button
              flex={1}
              py="10px"
              variant="subtle"
              colorScheme="edit"
              onClick={downloadRecoveryFile}
            >
              {t("common:download")}
            </Button>
            <Button
              flex={1}
              py="10px"
              variant="subtle"
              colorScheme="edit"
              onClick={copyRecoveryFile}
            >
              {t("common:copy")}
            </Button>
          </HStack>
        </Box>
      </CardBody>
      <CardFooter pt={0}>
        <AuthHelpButton />
        {onSuccess && (
          <Button onClick={onSuccess}>{t("common:continue")}</Button>
        )}
      </CardFooter>
    </Card>
  );
};
